import {
  Box,
  Button,
  Grid,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { SlideAnimationContext } from "contexts/SlideAnimation";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  OrganizationVaProfile,
  VaOrganizationProfileStatus,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import ConnectGmailDialog from "components/ConnectGmailDialog";
import { BENTO_VA_ONBOARDING_CALENDLY_LINK } from "constants/influencer";
import { getSource, trackEvent } from "utils/tracking";
import { useTracking } from "utils/useTracking";

import FAQ from "./FAQ";
import styles from "./styles";

interface Props {
  boughtTemplates: boolean;
  bookedCall: boolean;
  saveOrgVaProfile: (
    organizationVaProfile: OrganizationVaProfile,
    excludeIncludeFilterHasChanged?: boolean,
  ) => void;
  organizationVaProfile: OrganizationVaProfile | null;
  loading: boolean;
}

const SelfServeVaComplete = ({
  boughtTemplates,
  bookedCall,
  organizationVaProfile,
  saveOrgVaProfile,
  loading,
}: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const slideContext = useContext(SlideAnimationContext);
  const { trackVaStep } = useTracking();
  const source = getSource();

  const { currentOrg } = useContext(OrganizationUserContext);
  const { emailHealth, fetchIntegrationHealth, userIntegration, scopes } =
    useContext(UserIntegrationsContext);
  const { userIntegrations } = useContext(OrganizationUserContext);
  const [activeStep, setActiveStep] = useState(0);
  const [userIntegrationDialogState, setUserIntegrationDialogState] =
    useState(false);

  const handleBookCall = () => {
    trackEvent("VA Onboarding Flow - Book Call Button Clicked");
  };

  const changeOnboardingStatusToComplete = async () => {
    if (
      organizationVaProfile?.status !== VaOrganizationProfileStatus.completed
    ) {
      const updatedOrganizationVaProfile = {
        ...organizationVaProfile,
        status: VaOrganizationProfileStatus.completed,
      };
      saveOrgVaProfile(updatedOrganizationVaProfile);
    }
  };

  const handleNext = async () => {
    if (slideContext) {
      await slideContext.slideOutUp();
      slideContext.slideInUp();
    }
    trackVaStep(location.pathname, source);
    navigate(`/${routes.dashboard}${location.search}`);
  };

  const steps = [
    {
      label: "Connect your Gmail Account and provide Read + Write Access",
      description: `Providing access to your Gmail account allows Bento's virtual assistant to send emails on your behalf, automate follow-ups, improve your response rates and send you reminders.`,
    },
    {
      label: "You're all set! 💯",
      description: <></>,
    },
  ];

  const connectToGmail = () => {
    trackEvent("VA Onboarding Flow - Connect to Gmail Button Clicked");
    setUserIntegrationDialogState(true);
  };

  useEffect(() => {
    if (loading) {
      return;
    } else if (emailHealth) {
      changeOnboardingStatusToComplete();
      setActiveStep(2);
    } else {
      setActiveStep(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailHealth, loading]);

  useEffect(() => {
    if (currentOrg?.id) {
      fetchIntegrationHealth(currentOrg.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const onEmailConnected = async () => {
    setUserIntegrationDialogState(false);
    setActiveStep(1);
  };

  return (
    <>
      <Typography variant="h3" gutterBottom sx={{ textAlign: "center", mt: 0 }}>
        {emailHealth ? "Onboarding is now complete" : "You're almost there!"}
      </Typography>

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel>{step.label}</StepLabel>
            <StepContent>
              <Typography sx={styles.description}>
                {step.description}
              </Typography>

              {activeStep === 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={connectToGmail}
                  size="small"
                  disableElevation
                  sx={{ mr: 1, mt: 1 }}
                >
                  Connect
                </Button>
              )}
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {activeStep > 0 && (
        <Typography sx={[styles.description, { ml: 4 }]}>
          {boughtTemplates ? (
            <>
              Bento's Team will now begin writing your pitch templates. You will
              receive an email in about a week to review your templates to
              approve them.
            </>
          ) : (
            <>
              Bento Team's will now begin settings up your account. Emails will
              begin scheduling in the next 7 business days. You will be able to
              see them in your <strong>Inbox &#8250; Scheduled</strong> folder.
              You will receive an email when the first emails have been
              scheduled.{" "}
              {bookedCall
                ? "Use the button below to schedule your onboarding call"
                : ""}
            </>
          )}
        </Typography>
      )}

      {bookedCall && activeStep > 0 && (
        <Box sx={{ ml: 4, mt: 2 }}>
          <Button
            onClick={handleBookCall}
            target="_blank"
            href={BENTO_VA_ONBOARDING_CALENDLY_LINK}
            variant="contained"
            size="small"
            disableElevation
          >
            Book Call
          </Button>
        </Box>
      )}

      {activeStep > 0 && <FAQ boughtTemplates={boughtTemplates} />}

      <Grid item xs="auto" container columnGap={1} sx={{ mt: 4 }}>
        <Grid item xs></Grid>
        <Grid item xs="auto">
          <Button disableElevation disabled={!emailHealth} onClick={handleNext}>
            Done
          </Button>
        </Grid>
      </Grid>

      {/* Create a custom gmail connect in order to perform action automatically
        after connecting */}
      <ConnectGmailDialog
        openIntegrationDialog={userIntegrationDialogState}
        setOpenIntegrationDialog={(newOpen: boolean) =>
          setUserIntegrationDialogState(newOpen)
        }
        userIntegrations={userIntegrations}
        userIntegration={userIntegration}
        scopes={scopes}
        emailHealth={emailHealth}
        onSuccessAction={onEmailConnected}
        title="Connect your Gmail account"
      />
    </>
  );
};

export default SelfServeVaComplete;
